import { SupportedLocale, FALLBACK_LOCALE } from './config';

export type MessageMap = Record<string, string>;

const cache = new Map<string, MessageMap>();

async function loadModule(locale: SupportedLocale, moduleName: string): Promise<MessageMap> {
    const key = `${locale}/${moduleName}`;
    const cached = cache.get(key);
    if (cached) return cached;

    try {
        // webpack/CRA handles code splitting per locale+module combination.
        const data = await import(
            /* webpackChunkName: "locale-[request]" */
            `../locales/${locale}/${moduleName}.json`
        );
        const messages = ((data && (data as any).default) || data) as MessageMap;
        cache.set(key, messages);
        return messages;
    } catch {
        // Missing file — cache empty so repeated lookups don't rehit the loader.
        cache.set(key, {});
        return {};
    }
}

export async function loadLocaleMessages(
    locale: SupportedLocale,
    modules: string[] = ['common'],
): Promise<MessageMap> {
    const needsFallback = locale !== FALLBACK_LOCALE;

    const [primaryBundles, fallbackBundles] = await Promise.all([
        Promise.all(modules.map((m) => loadModule(locale, m))),
        needsFallback
            ? Promise.all(modules.map((m) => loadModule(FALLBACK_LOCALE, m)))
            : Promise.resolve<MessageMap[]>([]),
    ]);

    const fallback: MessageMap = Object.assign({}, ...fallbackBundles);
    const primary: MessageMap = Object.assign({}, ...primaryBundles);

    // Fallback first, primary overrides — so a missing key in `locale` transparently
    // falls through to English before reaching react-intl's onError handler.
    return { ...fallback, ...primary };
}

export function clearLocaleCache(): void {
    cache.clear();
}
